import { useQueries, useQuery } from "@tanstack/react-query";
import { getJson } from "../../lib/http";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import { MISSING_EXIT_AFTER_HOURS } from "../../store/oasis/yardInspectionsStore";
import {
  OPERATION_TOP_N,
  isTransientYardMetricsError,
  shouldRetryYardMetrics,
  visitsToStatusCounts,
  yardLocalDay,
  type YardDayRange,
  type YardMetricsResponse,
  type YardStatusCounts,
} from "../../pages/patios-maniobras/yardsSection/yardOperation";

/**
 * Cada cuánto se repinta la operación del patio.
 *
 * 30 s, no los 5 s de `POLL_INTERVAL_MS` de la pantalla de accesos: esa es una
 * consola de portería —un guardia mirando pasar un camión ahora mismo— y ésta
 * es un panorama de N patios. Es el mismo intervalo que ya usa el refetch de
 * dispositivos (accessControlSection/page.tsx:1880-1889), así que un operador
 * que tiene las dos pantallas abiertas no ve dos relojes distintos.
 */
export const YARD_METRICS_POLL_MS = 30_000;

/**
 * ── Por qué N requests y no uno ──────────────────────────────────────────────
 *
 * `/yardMetrics` exige `yard_id` obligatorio y único (yardMetricsSchema.js), y
 * eso no es un descuido: es lo que mantiene la consulta indexada y acotada, y
 * está bajo review en el PR de #500. O sea que pintar la tira de conteos de la
 * página cuesta un request por patio, repetido cada 30 s. Eso roza el N+1 que
 * este hito viene a evitar, así que la decisión se tomó MIDIENDO, no estimando.
 *
 * Medido el 4-sep-2026 contra las dos bases reales (script read-only sobre
 * `truck_yards`, mismo criterio de borrado que el listado):
 *
 *   • producción (dumax-lite-production): 7 patios, todos de una sola empresa,
 *     ninguno legacy sin `companyId`;
 *   • dev (tdc-development-846de): 8.
 *
 * Y lo que se pinta de una vez es menos que eso: la pantalla pagina de a
 * PAGE_SIZE = 10 en el cliente, y el listado del backend trae 20 por página.
 * O sea que el techo real hoy son **10 requests cada 30 s** — 0,33 req/s por
 * pestaña abierta— y el número de hoy es 7. Cada uno lee las inspecciones de UN
 * patio en UN día: producción tiene 149 inspecciones en total (todas de
 * Acuapue), así que un día es del orden de unidades de documentos.
 *
 * Sólo se piden las métricas de los patios de la página visible, y por eso el
 * hook recibe `yards` ya paginado. Con la lista completa serían 7 requests para
 * pintar 10 tarjetas hoy, y N para pintar 10 cuando el cliente crezca.
 *
 * Cuándo dejar de hacer esto: cuando una página deje de ser la unidad. Mientras
 * la página tope en 10-20 tarjetas, N está acotado por el paginado y no por el
 * tamaño del cliente. Si mañana alguien sube PAGE_SIZE, o si un tablero quiere
 * los conteos de TODOS los patios a la vez, el arreglo NO es subir la
 * paralelización: es el rollup por empresa de #503, que suma patio por patio
 * sobre particiones disjuntas y ya está pensado para eso. Cambiar el contrato
 * de `/yardMetrics` para aceptar varios `yard_id` sería peor: rompe el recorte
 * por patio del usuario (#495), que hoy es un 403 limpio por patio ajeno.
 */
export interface YardMetricsEntry {
  counts: YardStatusCounts | null;
  total: number;
  isLoading: boolean;
  isError: boolean;
  /** El día del patio que se consultó, "YYYY-MM-DD". Va a la etiqueta de la tarjeta. */
  day: string;
}

export type YardMetricsByYard = Record<string, YardMetricsEntry>;

/**
 * `polling` apaga el REFRESCO, no la carga inicial.
 *
 * La distinción importa y costó un caso real: con `enabled: false` una consulta
 * de TanStack sin datos en caché queda en `isPending`, o sea que las tarjetas
 * dirían "Cargando conteos…" indefinidamente sin que nada se esté cargando. Se
 * llega ahí por el camino más común de esta pantalla: el enlace profundo del
 * panel de geocercas (`?open=<yardId>`) abre la modal de detalles ANTES de que
 * exista una primera respuesta, y las tarjetas de atrás quedaban colgadas en
 * "cargando" hasta que alguien cerrara la modal. Lo que hay que evitar mientras
 * una modal está abierta es el refetch repetido, no el primero.
 */
export function useYardMetrics(
  yards: { id: string; timeZone?: string }[],
  { polling = true }: { polling?: boolean } = {},
): YardMetricsByYard {
  const companyId = useCompaniesStore((s) => s.companyId);

  const days = yards.map((y) => yardLocalDay(y.timeZone));

  const results = useQueries({
    queries: yards.map((yard, i) => ({
      // El día entra en la clave: a la medianoche del patio la consulta cambia
      // sola en vez de seguir mostrando la de ayer hasta que alguien recargue.
      queryKey: ["yardMetrics", companyId, yard.id, days[i]] as const,
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        getJson<{ data: YardMetricsResponse }>(
          "/yardMetrics",
          {
            yard_id: yard.id,
            date_from: days[i],
            date_to: days[i],
            // El umbral de "sin salida" lo manda el cliente porque el
            // vocabulario de estados es del cliente: el servidor hace la
            // aritmética con ESTE número (yardMetricsController.js:113-119) y lo
            // devuelve, de modo que la tarjeta y la grilla del historial no
            // pueden discrepar por tener dos constantes distintas.
            missing_exit_after_hours: MISSING_EXIT_AFTER_HOURS,
          },
          { signal },
        )
          // `response()` envuelve todo en `{ meta, data }`; acá sólo interesa el
          // cuerpo. Un patio ajeno (403) o inexistente (404) tira, y la tarjeta
          // muestra "conteos no disponibles" en vez de un cero que se leería
          // como "hoy no entró nadie".
          .then((body) => body.data),
      // Sin empresa todavía no se pide nada: el interceptor de `api` inyecta
      // `x-company-id` desde este mismo store, y sin él el endpoint responde
      // 400 companyIdRequired. Disparar N requests condenados durante el
      // hidratado del layout deja las tarjetas en "no disponibles" hasta el
      // refetch siguiente, cuando lo cierto es que todavía están cargando.
      enabled: !!yard.id && !!companyId,
      staleTime: YARD_METRICS_POLL_MS,
      refetchInterval: polling ? YARD_METRICS_POLL_MS : (false as const),
      // Pausa con la pestaña oculta. TanStack corta el intervalo cuando la
      // ventana pierde el foco/visibilidad, que es el mismo mecanismo que ya
      // usa el refetch de dispositivos; no hace falta un listener propio de
      // `visibilitychange` como el de accessSection.
      refetchIntervalInBackground: false,
      // Un patio ajeno responde 403 y un yard_id inexistente 404
      // (yardMetricsController). Ninguno de los dos mejora reintentando, y
      // reintentar tres veces por patio multiplica por tres el peor caso.
      //
      // Acá se queda en 0 incluso para los fallos transitorios, a diferencia del
      // panel de abajo, y la diferencia es que ESTA consulta se cura sola: la
      // tarjeta es siempre el día en curso, así que tiene `refetchInterval` de
      // 30 s y un corte de red se borra en el refresco siguiente. La única
      // ventana sin intervalo es con una modal abierta (`polling: false`), y ahí
      // las tarjetas están tapadas por la modal y el intervalo vuelve al
      // cerrarla. Sumarle reintentos multiplicaría por tres el peor caso de la
      // página —hasta 10 patios a la vez— para arreglar algo que no está roto.
      retry: 0,
    })),
  });

  const byYard: YardMetricsByYard = {};
  yards.forEach((yard, i) => {
    const r = results[i];
    const visits = r?.data?.visits;
    byYard[yard.id] = {
      counts: visits ? visitsToStatusCounts(visits) : null,
      total: visits?.total ?? 0,
      // `isPending` y no `isFetching`: mientras haya datos anteriores la
      // tarjeta sigue mostrando los conteos y el refresco de cada 30 s pasa
      // desapercibido. Un spinner cada medio minuto en diez tarjetas sería la
      // razón por la que alguien apaga esta pantalla.
      isLoading: !!r?.isPending,
      isError: !!r?.isError,
      day: days[i],
    };
  });
  return byYard;
}

/**
 * ── El detalle de UN patio, para el panel de operación de la modal (#501) ────
 *
 * Misma puerta que la tarjeta: el mismo `GET /yardMetrics`, el mismo
 * `getJson`, el mismo umbral de "sin salida" y el mismo intervalo. No se abre
 * una segunda vía de fetch, porque la razón por la que la tarjeta y la grilla
 * del historial no pueden discrepar —el servidor hace la aritmética con el
 * umbral que manda el CLIENTE— vale igual para el panel.
 *
 * Lo que sí cambia, y por eso es otro hook y no un parámetro más de
 * `useYardMetrics`:
 *
 *  1. **El rango.** La tarjeta es siempre el día en curso del patio; el panel
 *     tiene selector. `from` y `to` entran en la clave.
 *  2. **`top`.** El panel grafica top 10 + "otros" y la tarjeta no grafica
 *     nada, así que pide `top=OPERATION_TOP_N` y la tarjeta se queda con el
 *     default de 5. Eso hace que las dos respuestas sean CUERPOS DISTINTOS para
 *     el mismo patio y el mismo día, y por eso `top` entra en la clave: con una
 *     clave compartida, el que llegara segundo se comería la caché del primero
 *     y el panel se quedaría con cinco barras sin que nada lo delate.
 *  3. **N = 1.** La tarjeta paga un request por patio de la página (hasta 10);
 *     el panel es uno solo, el que está abierto.
 *
 * El costo de no compartir caché es exactamente un request extra mientras la
 * modal está abierta, sobre UN patio. Y ni siquiera se suma al polling de las
 * tarjetas: `page.tsx` apaga el refresco de la tira de conteos con cualquier
 * modal abierta (`polling = !detailsYard && !formOpen`), así que mientras el
 * panel refresca, las diez tarjetas de atrás no.
 */
export interface YardOperationMetrics {
  data: YardMetricsResponse | null;
  isLoading: boolean;
  isError: boolean;
  /** Se está refrescando con datos ya en pantalla. El panel no parpadea por esto. */
  isFetching: boolean;
  /**
   * Reintento explícito, para el botón del estado de error.
   *
   * Es la única salida que le queda al usuario cuando el fallo NO fue
   * transitorio pero él sabe que algo cambió afuera (le acaban de dar acceso al
   * patio, el backend volvió). Devuelve void a propósito: el panel no espera el
   * resultado, lo lee del propio hook en el render siguiente.
   */
  retry: () => void;
}

export function useYardOperationMetrics(
  yard: { id: string; timeZone?: string } | null | undefined,
  // `null` es "el rango personalizado está a medio escribir" (yardCustomRange).
  // No es lo mismo que un rango vacío: con un extremo sin forma de día el
  // backend arma la consulta SIN ese límite y devuelve el histórico entero,
  // así que acá no se pide nada en vez de pedir de más.
  range: YardDayRange | null,
): YardOperationMetrics {
  const companyId = useCompaniesStore((s) => s.companyId);

  // Un rango que TERMINA hoy todavía puede cambiar mientras la modal está
  // abierta —un camión entra, otro sale— así que se refresca. Uno que terminó
  // ayer o antes está anclado a `entry_timestamp` (yardMetricsController.js
  // devuelve `anchor`), así que ninguna visita nueva puede caer adentro y
  // repetir el request cada 30 s sería puro gasto.
  //
  // La excepción conocida, y por qué no se arregla acá: una visita que entró
  // ayer y sale hoy SÍ cambia el promedio de estancia de ayer. Es un caso que
  // se corrige solo al reabrir la modal o al mover el selector, y perseguirlo
  // con polling sobre rangos de 30 días costaría un barrido completo cada 30 s
  // para mover un promedio en minutos.
  const endsToday = !!range && range.to >= yardLocalDay(yard?.timeZone);

  const query = useQuery({
    queryKey: [
      "yardMetrics",
      companyId,
      yard?.id,
      range?.from ?? null,
      range?.to ?? null,
      OPERATION_TOP_N,
    ] as const,
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      getJson<{ data: YardMetricsResponse }>(
        "/yardMetrics",
        {
          yard_id: yard?.id,
          date_from: range?.from,
          date_to: range?.to,
          missing_exit_after_hours: MISSING_EXIT_AFTER_HOURS,
          top: OPERATION_TOP_N,
        },
        { signal },
      ).then((body) => body.data),
    // Sin empresa el interceptor de `api` no inyecta `x-company-id` y el
    // endpoint responde 400 companyIdRequired; ver la nota de useYardMetrics.
    enabled: !!yard?.id && !!companyId && !!range,
    staleTime: YARD_METRICS_POLL_MS,
    refetchInterval: endsToday ? YARD_METRICS_POLL_MS : (false as const),
    refetchIntervalInBackground: false,
    // 403 (patio ajeno al usuario) y 404 (patio inexistente o de otra empresa)
    // no mejoran reintentando, y un 422 por un rango ilegible tampoco. Un fallo
    // de red o un 5xx sí: ver `shouldRetryYardMetrics`, que es donde está
    // escrito el porqué de la distinción.
    //
    // Este es el hook donde hacía falta, y no el de las tarjetas: un rango que
    // ya terminó tiene `refetchInterval: false` —a propósito, ninguna visita
    // nueva puede caer adentro— así que sin reintento no había NINGUNA vía de
    // recuperación salvo mover el selector, porque `from`/`to` están en la
    // clave. Un parpadeo de red dejaba el panel muerto.
    retry: shouldRetryYardMetrics,
    // La app entera va con `refetchOnWindowFocus: false` (lib/queryClient.ts:9)
    // y eso no se toca: es la política global y un panel no la cambia. Acá se
    // abre la excepción MÁS ANGOSTA posible —sólo si el último intento falló, y
    // sólo si falló de forma transitoria— para que volver a la pestaña recupere
    // un panel que se cayó por un corte de red. Con un 403 del recorte por patio
    // devuelve false, así que un panel abierto sobre un patio ajeno no le pega
    // al backend cada vez que alguien hace alt-tab.
    refetchOnWindowFocus: (q) =>
      q.state.status === "error" && isTransientYardMetricsError(q.state.error),
  });

  return {
    data: query.data ?? null,
    // `isPending` y no `isFetching`: mientras haya datos anteriores el panel
    // sigue mostrando los números y el refresco de cada 30 s pasa desapercibido.
    //
    // Y sin mirar `fetchStatus`: mientras `enabled` es false —o sea, mientras el
    // layout todavía no hidrató la empresa— la consulta queda pendiente y con
    // fetchStatus "idle", y distinguirlo dejaría el panel mostrando "conteos no
    // disponibles" durante el hidratado. Eso es un error que no ocurrió; lo
    // cierto es que todavía está cargando.
    isLoading: query.isPending,
    isError: query.isError,
    isFetching: query.isFetching,
    // `refetch` y no `invalidateQueries`: hay que volver a pedir ESTA consulta,
    // no marcar como viejas todas las que empiezan con "yardMetrics" — eso
    // arrastraría las hasta 10 tarjetas de atrás por un botón del panel.
    retry: () => {
      void query.refetch();
    },
  };
}
